import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { PointsRing } from '../components/PointsRing';
import { Timeline } from '../components/Timeline';
import { ErrorState, Loading } from '../components/StateBlock';
import { useAsync } from '../hooks/useAsync';
import { usePolling } from '../hooks/usePolling';
import { useElapsedSeconds } from '../hooks/useTimers';
import { getAttendance, getChallenge } from '../api';
import { calcPoints } from '../utils/points';
import { fmtClock } from '../utils/format';
import { paths } from '../paths';
import { Challenge } from '../types';

function InProgressView({ challenge }: { challenge: Challenge }) {
  const navigate = useNavigate();
  const { rules, attendance, id } = challenge;
  const minStay = rules.minStayMinutes ?? 0;

  const elapsed = useElapsedSeconds(attendance.checkInAt);
  const previewPoints = calcPoints(Math.floor(elapsed / 60), rules);
  const reachedMin = elapsed >= minStay * 60;
  const minutesLeft = Math.max(0, Math.ceil((minStay * 60 - elapsed) / 60));

  // If the check-out is registered (or the day ends), leave this screen.
  usePolling(async () => {
    const a = await getAttendance(id);
    if (a.status === 'COMPLETED') navigate(paths.result(id), { replace: true });
    if (a.status === 'MISSED') navigate(paths.detail(id), { replace: true });
  }, 15000);

  return (
    <div className="pv-container pv-inprogress">
      <header className="pv-inprogress__header">
        <Link to={paths.detail(id)} className="pv-icon-btn" aria-label="Voltar">
          <Icon name="back" size={22} />
        </Link>
        <h1 className="pv-h3">{challenge.title}</h1>
        <span className="pv-pill pv-pill--success">No local</span>
      </header>

      <section className="pv-card pv-center pv-stack">
        <PointsRing
          elapsedSeconds={elapsed}
          minStayMinutes={rules.minStayMinutes}
        />

        <div className="pv-twin">
          <div className="pv-twin__a">
            <span className="pv-small">Você já tem</span>
            <strong>+{previewPoints} pts</strong>
          </div>
          <div className="pv-twin__b">
            <span className="pv-small pv-muted">
              {reachedMin ? 'Meta' : `Faltam para os ${rules.maxPoints}`}
            </span>
            <strong>{reachedMin ? 'Atingida' : `${minutesLeft} min`}</strong>
          </div>
        </div>
        {reachedMin && (
          <p className="pv-muted pv-small">
            Você já garantiu todos os pontos. Registre a saída ao ir embora.
          </p>
        )}
      </section>

      <Timeline
        items={[
          {
            state: 'done',
            title: 'Entrada registrada',
            text: `${fmtClock(attendance.checkInAt)} · validada por ${attendance.validatedBy ?? 'seu gestor'}`,
          },
          {
            state: 'current',
            title: 'Permanência em andamento',
            text: 'Os pontos crescem conforme você fica no local.',
          },
          {
            state: 'todo',
            title: 'Saída',
            text: 'Antes de ir embora, gere o QR de saída para receber seus pontos.',
          },
        ]}
      />

      <div className="pv-cta-bar pv-cta-bar--always">
        <Link
          to={paths.checkOut(id)}
          className="pv-btn pv-btn--primary pv-btn--grow"
        >
          <Icon name="qr" size={20} />
          Gerar QR Code de saída
        </Link>
      </div>
    </div>
  );
}

/** Stay in progress: shown after the manager registers the check-in. */
export function InProgressPage() {
  const { eventId = '' } = useParams();
  const {
    data: challenge,
    loading,
    error,
    reload,
  } = useAsync(() => getChallenge(eventId), [eventId]);

  if (loading && !challenge) return <Loading />;
  if (error || !challenge) return <ErrorState onRetry={reload} />;

  const status = challenge.attendance?.status;
  if (status === 'COMPLETED')
    return <Navigate to={paths.result(eventId)} replace />;
  if (status !== 'CHECKED_IN')
    return <Navigate to={paths.detail(eventId)} replace />;

  return <InProgressView challenge={challenge} />;
}
